/**
 * The desktop app draws its own title bar (as Claude's app does): a slim strip on
 * the warm canvas to drag the window (double-click maximises), with the window
 * buttons on the right. The system title bar is off (`decorations: false`).
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Minus, Square, X } from 'lucide-react';
import { getCurrentWindow } from '@tauri-apps/api/window';
import { cn } from '../lib/cn';

const button =
  'grid h-full w-11 place-items-center text-fg-muted transition-colors hover:bg-bg-hover hover:text-fg';

export function TitleBar() {
  const { t } = useTranslation();
  const [maximized, setMaximized] = useState(false);

  useEffect(() => {
    const win = getCurrentWindow();
    let off: (() => void) | undefined;
    const check = () => void win.isMaximized().then(setMaximized);
    check();
    void win.onResized(check).then((un) => (off = un));
    return () => off?.();
  }, []);

  const win = () => getCurrentWindow();
  return (
    <div
      data-tauri-drag-region
      className="flex h-9 shrink-0 items-stretch bg-app select-none"
      role="toolbar"
      aria-label={t('window.bar')}
    >
      <div data-tauri-drag-region className="flex-1" />
      <button
        type="button"
        aria-label={t('window.minimize')}
        title={t('window.minimize')}
        onClick={() => void win().minimize()}
        className={button}
      >
        <Minus className="size-4" />
      </button>
      <button
        type="button"
        aria-label={maximized ? t('window.restore') : t('window.maximize')}
        title={maximized ? t('window.restore') : t('window.maximize')}
        onClick={() => void win().toggleMaximize()}
        className={button}
      >
        {maximized ? <Copy className="size-3.5 -scale-x-100" /> : <Square className="size-3.5" />}
      </button>
      <button
        type="button"
        aria-label={t('window.close')}
        title={t('window.close')}
        onClick={() => void win().close()}
        className={cn(button, 'hover:bg-[#c42b1c] hover:text-white')}
      >
        <X className="size-4" />
      </button>
    </div>
  );
}
